/* eslint-disable react/jsx-no-bind */
import { FilterList, Clear, Payment } from '@mui/icons-material';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Avatar,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import Header from '../Header';

import PaymentAlertsService from './paymentAlertsService';

const cardShadow = '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)';
const cardShadowHover =
  '0 2px 6px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)';
const radiusSm = 3;
const radiusMd = 4;
const radiusLg = 6;

const PaymentAlerts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Staged filter values (what user is typing)
  const [filter, setFilter] = useState('');
  const [tripId, setTripId] = useState('');
  const [shipmentId, setShipmentId] = useState('');
  const [tripShipmentId, setTripShipmentId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Applied filter values (used in API calls)
  const [appliedFilter, setAppliedFilter] = useState('');
  const [appliedTripId, setAppliedTripId] = useState('');
  const [appliedShipmentId, setAppliedShipmentId] = useState('');
  const [appliedTripShipmentId, setAppliedTripShipmentId] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');

  const filterOptions = [
    { value: '', label: 'All Alerts' },
    { value: 'payment_pending', label: 'Payment Pending' },
    { value: 'payment_failed', label: 'Payment Failed' },
    { value: 'refund_pending', label: 'Refund Pending' },
    { value: 'refund_failed', label: 'Refund Failed' },
    { value: 'payout_failed', label: 'Payout Failed' },
    { value: 'payout_pending', label: 'Payout Pending' },
  ];

  const getStatusColor = (alertType) => {
    switch (alertType) {
      case 'payment_pending':
        return 'warning';
      case 'payment_failed':
        return 'error';
      case 'refund_pending':
        return 'info';
      case 'refund_failed':
        return 'error';
      case 'payout_failed':
        return 'error';
      case 'payout_pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await PaymentAlertsService.fetchAlerts(
        page + 1,
        rowsPerPage,
        appliedFilter,
        appliedTripId,
        appliedShipmentId,
        appliedTripShipmentId,
        appliedFromDate,
        appliedToDate,
      );
      setAlerts(response.data?.alerts ?? []);
      setTotalCount(response.data?.totalCount ?? 0);
      setError(null);
    } catch (err) {
      setError('Failed to fetch payment alerts');
      logger.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    appliedFilter,
    appliedTripId,
    appliedShipmentId,
    appliedTripShipmentId,
    appliedFromDate,
    appliedToDate,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilter(event.target.value);
  };

  const handleApplyFilters = () => {
    setAppliedFilter(filter);
    setAppliedTripId(tripId);
    setAppliedShipmentId(shipmentId);
    setAppliedTripShipmentId(tripShipmentId);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilter('');
    setTripId('');
    setShipmentId('');
    setTripShipmentId('');
    setFromDate('');
    setToDate('');
    setAppliedFilter('');
    setAppliedTripId('');
    setAppliedShipmentId('');
    setAppliedTripShipmentId('');
    setAppliedFromDate('');
    setAppliedToDate('');
    setPage(0);
  };

  const formatDate = (dateString) => {
    return formatToIST(dateString);
  };

  const formatAmount = (amount) => {
    return amount ? `₹${amount.toFixed(2)}` : 'N/A';
  };

  const getStatusValue = (alert) => {
    const raw = alert.status ?? alert.paymentStatus ?? alert.alertStatus ?? '';
    if (!raw) return '—';
    return String(raw)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const inputHeight = 44;
  const filterPaperSx = {
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    borderRadius: radiusLg,
    boxShadow: cardShadow,
  };
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: inputHeight,
      borderRadius: radiusSm,
      backgroundColor: isDark ? colors.primary[500] : '#ffffff',
      fontSize: '0.9375rem',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      '& fieldset': {
        borderColor: 'divider',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: isDark ? colors.swiftcarry[100] : 'rgba(0, 201, 150, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderWidth: '2px',
        borderColor: isDark ? colors.swiftcarry[100] : 'rgba(0, 201, 150, 0.6)',
        boxShadow: `0 0 0 3px ${
          isDark ? 'rgba(0, 201, 150, 0.15)' : 'rgba(0, 201, 150, 0.12)'
        }`,
      },
      '& input': {
        py: 1.5,
        px: 1.5,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.9375rem',
    },
  };
  const primaryButtonSx = {
    height: inputHeight,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: radiusSm,
    boxShadow: '0 2px 6px rgba(0, 201, 150, 0.25)',
    bgcolor: colors.swiftcarry[100],
    '&:hover': {
      bgcolor:
        theme.palette.mode === 'dark' ? colors.swiftcarry[100] : '#00a67d',
      boxShadow: '0 4px 12px rgba(0, 201, 150, 0.35)',
    },
  };
  const secondaryButtonSx = {
    height: inputHeight,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: radiusSm,
    borderColor: 'divider',
    '&:hover': {
      borderColor: 'action.selected',
      bgcolor: 'action.hover',
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          m: { xs: 1.5, sm: 2, md: 3 },
          pb: { xs: 4, md: 2 },
        }}
      >
        <Header
          title="Payment Alerts"
          subtitle="Monitor payment-related alerts and issues"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            backgroundColor: 'background.paper',
            borderRadius: radiusLg,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: cardShadow,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={48} sx={{ color: 'primary.main' }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Loading alerts…
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        m: { xs: 1.5, sm: 2, md: 3 },
        pb: { xs: 4, md: 2 },
      }}
    >
      <Header
        title="Payment Alerts"
        subtitle="Monitor payment-related alerts and issues"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: radiusLg }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5, md: 3 },
          borderRadius: radiusLg,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: cardShadow,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 2,
            flexWrap: 'wrap',
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: radiusSm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
              color: 'primary.main',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              flexShrink: 0,
            }}
          >
            <FilterList sx={{ fontSize: 20 }} />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight="700"
            color="text.primary"
            sx={{ flexShrink: 0 }}
          >
            Filters
          </Typography>
        </Box>
        <Grid container spacing={1.5} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={inputSx}>
              <InputLabel id="payment-alerts-filter-label">
                Alert type
              </InputLabel>
              <Select
                value={filter}
                labelId="payment-alerts-filter-label"
                label="Alert type"
                onChange={handleFilterChange}
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Trip ID"
              type="number"
              value={tripId}
              onChange={(e) => setTripId(e.target.value)}
              placeholder="Trip ID"
              inputProps={{ min: 1, step: 1 }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Shipment ID"
              type="number"
              value={shipmentId}
              onChange={(e) => setShipmentId(e.target.value)}
              placeholder="Shipment ID"
              inputProps={{ min: 1, step: 1 }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Booking ID"
              type="number"
              value={tripShipmentId}
              onChange={(e) => setTripShipmentId(e.target.value)}
              placeholder="Booking ID"
              inputProps={{ min: 1, step: 1 }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: toDate || undefined }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: fromDate || undefined }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<FilterList />}
              onClick={handleApplyFilters}
              sx={primaryButtonSx}
            >
              Apply
            </Button>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClearAllFilters}
              sx={secondaryButtonSx}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {alerts.map((alert) => (
          <Grid item xs={12} sm={6} md={4} key={alert.alertId}>
            <Card
              elevation={0}
              variant="outlined"
              sx={{
                height: '100%',
                borderRadius: radiusLg,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: cardShadow,
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  borderColor: 'action.selected',
                  boxShadow: cardShadowHover,
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    color="text.primary"
                    noWrap
                    sx={{
                      fontSize: { xs: '0.9375rem', sm: '1rem' },
                      letterSpacing: '0.01em',
                      minWidth: 0,
                      flex: '1 1 auto',
                    }}
                  >
                    Alert #{alert.alertId}
                  </Typography>
                  <Chip
                    label={alert.alertType.replace(/_/g, ' ').toUpperCase()}
                    color={getStatusColor(alert.alertType)}
                    size="small"
                    sx={{
                      borderRadius: radiusSm,
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      flexShrink: 0,
                      '& .MuiChip-label': { px: { xs: 1, sm: 1.5 } },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, sm: 2 },
                    mb: 2,
                    py: 1.25,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{
                        mb: 0.25,
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Booking ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        fontSize: '0.8125rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {alert.bookingId ?? '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{
                        mb: 0.25,
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Shipment ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        fontSize: '0.8125rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {alert.shipmentId ?? '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{
                        mb: 0.25,
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Trip ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{
                        fontSize: '0.8125rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {alert.tripId ?? '—'}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: radiusMd,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 72,
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        fontWeight="600"
                        sx={{
                          mb: 0.5,
                          fontSize: '0.6875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Sender
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight="600"
                        sx={{
                          fontSize: '0.8125rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {alert.sender?.name || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.6875rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          display: 'block',
                        }}
                      >
                        {alert.sender?.phone || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: radiusMd,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 72,
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        fontWeight="600"
                        sx={{
                          mb: 0.5,
                          fontSize: '0.6875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Traveler
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight="600"
                        sx={{
                          fontSize: '0.8125rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {alert.traveler?.name || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.6875rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          display: 'block',
                        }}
                      >
                        {alert.traveler?.phone || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: radiusMd,
                        bgcolor: isDark
                          ? 'rgba(0, 201, 150, 0.06)'
                          : 'rgba(0, 201, 150, 0.04)',
                        border: '1px solid',
                        borderColor: isDark
                          ? 'rgba(0, 201, 150, 0.15)'
                          : 'rgba(0, 201, 150, 0.12)',
                        minHeight: 72,
                      }}
                    >
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        fontWeight="600"
                        sx={{
                          mb: 0.5,
                          fontSize: '0.6875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Amount
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color={colors.swiftcarry[100]}
                        fontWeight="600"
                        sx={{
                          fontSize: '0.9375rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {alert.amount ? formatAmount(alert.amount) : 'N/A'}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        fontWeight="600"
                        sx={{
                          mb: 0.5,
                          mt: 1,
                          fontSize: '0.6875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        Status
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight="600"
                        sx={{
                          fontSize: '0.8125rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {getStatusValue(alert)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.6875rem' }}
                  >
                    Created: {formatDate(alert.createdAt)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {alerts.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            ...filterPaperSx,
            mt: { xs: 2, md: 3 },
            py: { xs: 4, md: 8 },
            px: { xs: 2, md: 3 },
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              width: { xs: 56, md: 72 },
              height: { xs: 56, md: 72 },
              mx: 'auto',
              mb: 2,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              boxShadow: cardShadow,
            }}
          >
            <Payment sx={{ fontSize: { xs: 28, md: 40 } }} />
          </Avatar>
          <Typography
            variant="h6"
            fontWeight="700"
            color="text.primary"
            mb={1}
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              letterSpacing: '0.01em',
            }}
          >
            No payment alerts match your criteria
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: 360,
              mx: 'auto',
              px: { xs: 1, md: 0 },
              fontWeight: 500,
            }}
          >
            Try adjusting the filter or check back later.
          </Typography>
        </Paper>
      )}

      {totalCount > 0 && (
        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            mb: { xs: 2, md: 2 },
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            borderRadius: radiusLg,
            overflow: 'hidden',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              '& .MuiTablePagination-toolbar': {
                padding: '16px 24px',
                minHeight: '64px',
                justifyContent: 'space-between',
                alignItems: 'center',
              },
              '& .MuiTablePagination-selectLabel': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.secondary',
              },
              '& .MuiTablePagination-displayedRows': {
                marginBottom: 0,
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.secondary',
              },
              '& .MuiTablePagination-select': {
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'divider',
                padding: '4px 8px',
                marginLeft: '8px',
                marginRight: '16px',
              },
              '& .MuiTablePagination-actions': {
                marginLeft: '20px',
                '& .MuiIconButton-root': {
                  padding: '8px',
                  margin: '0 2px',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default PaymentAlerts;
